import { useEffect, useState } from 'react';

// Cache em módulo (sobrevive a remounts, evita rebuscar a mesma UF na mesma sessão).
const cache = new Map<string, string[]>();

interface UseBrazilCitiesResult {
  cities: string[];
  loading: boolean;
  /** true quando a busca falhou (rede/IBGE fora do ar) — quem usa o hook deve then
   * cair de volta pra um campo de texto livre, pra não travar o cadastro. */
  failed: boolean;
}

/**
 * Busca os municípios de uma UF via API pública do IBGE, pra alimentar um select
 * de cidade (evita erro de digitação que atrapalha a geocodificação do mapa astral).
 * Sem UF selecionada, retorna lista vazia sem chamar a API.
 */
export const useBrazilCities = (uf?: string | null): UseBrazilCitiesResult => {
  const [cities, setCities] = useState<string[]>(uf ? cache.get(uf) || [] : []);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!uf) {
      setCities([]);
      setFailed(false);
      return;
    }
    const cached = cache.get(uf);
    if (cached) {
      setCities(cached);
      setFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${uf}/municipios`)
      .then(r => {
        if (!r.ok) throw new Error(`IBGE respondeu ${r.status}`);
        return r.json();
      })
      .then((data: Array<{ nome: string }>) => {
        if (cancelled) return;
        const names = data.map(m => m.nome).sort((a, b) => a.localeCompare(b, 'pt-BR'));
        cache.set(uf, names);
        setCities(names);
      })
      .catch(() => {
        if (cancelled) return;
        setCities([]);
        setFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [uf]);

  return { cities, loading, failed };
};

export default useBrazilCities;
